package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.model.Shift

/**
 * SH-197: how shift writes are split into batched writes the rules can check.
 *
 * firestore.rules reads each person a written shift names (`userId`, `actualUserId`) to check they
 * work at its workplace (SH-175, assigneeWorksAt), and a batched write may make at most 20
 * document accesses in all: the caller's profile, the plan and the roster take 6, every further
 * person 1. A batch naming more than 14 different people is refused as a whole (before SH-197's
 * rule change: more than 7), so a copied week or an auto-assigned month is written in batches of
 * at most [MAX_PEOPLE] people (any number of shifts without a person) and [MAX_WRITES] writes.
 */
internal object ShiftBatches {
    /** Different people per batch: 14 fit the rules' budget; the rest is headroom. */
    const val MAX_PEOPLE = 10

    /** Writes per batch (Firestore allows 500). */
    const val MAX_WRITES = 450

    /** The people [shift] names for the rules. */
    fun peopleOf(shift: Shift): Set<String> = setOfNotNull(shift.userId, shift.actualUserId)

    /** [shifts] in order, in batches of at most [maxPeople] different people and [maxWrites] writes. */
    fun split(shifts: List<Shift>, maxPeople: Int = MAX_PEOPLE, maxWrites: Int = MAX_WRITES): List<List<Shift>> {
        val batches = mutableListOf<List<Shift>>()
        var current = mutableListOf<Shift>()
        var people = mutableSetOf<String>()
        for (shift in shifts) {
            val theirs = peopleOf(shift)
            val joined = people + theirs
            if (current.isNotEmpty() && (current.size >= maxWrites || joined.size > maxPeople)) {
                batches += current
                current = mutableListOf()
                people = mutableSetOf()
            }
            current += shift
            people += theirs
        }
        if (current.isNotEmpty()) batches += current
        return batches
    }
}
